import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { FlightFaresList } from "@/features/flights/components/flight-fares/FlightFaresList";
import { FlightFaresPageHeader } from "@/features/flights/components/flight-fares/FlightFaresPageHeader";
import type { FlightFare } from "@/features/flights/components/flight-fares/types";
import {
  getDepartureLegFromFare,
  useFlightFares,
} from "@/features/flights/components/flight-fares/useFlightFares";
import { useFlightFaresAccessGuard } from "@/features/flights/components/flight-fares/useFlightFaresAccessGuard";
import { useSelectedFlightOffer } from "@/features/flights/hooks/flightSearch/useSelectedFlightOffer";
import { formatCurrency } from "@/utils/currency";

export const Route = createFileRoute(
  "/_authenticated/flights/_flights/departure/fare",
)({
  component: RouteComponent,
});

export function RouteComponent() {
  useFlightFaresAccessGuard();

  const intl = useIntl();
  const navigate = Route.useNavigate();

  const { isRoundTrip, offer, departureFlight } = useSelectedFlightOffer();
  const {
    departureFares: fares,
    isFaresLoading,
    isFaresError,
    selectedFare,
    defaultFare,
    setSelectedFare,
  } = useFlightFares();

  const [tripFare, setTripFare] = useState<FlightFare>(
    selectedFare ?? defaultFare,
  );

  const handleContinue = () => {
    setSelectedFare(tripFare);

    if (isRoundTrip) {
      navigate({
        to: "/flights/return/fare",
      });
    } else {
      navigate({
        to: "/flights/offer/$id/step/1",
        params: { id: offer.index },
      });
    }
  };

  return (
    <main className="no-footer with-drawer main h-screen px-main pt-main">
      <div className="flex flex-col gap-4">
        <FlightFaresPageHeader
          header={intl.formatMessage({
            defaultMessage: "Select departure fare",
            description: "Page title when selecting the departure fare",
          })}
          flight={departureFlight}
          stepper={isRoundTrip && { currentStep: 1, totalSteps: 2 }}
        />

        <FlightFaresList
          fares={fares}
          getFareLeg={getDepartureLegFromFare}
          selectedFare={tripFare}
          onFareSelect={setTripFare}
          isLoading={isFaresLoading}
          isError={isFaresError}
        />
      </div>

      <PersistentDrawer>
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm font-medium text-text-primary">
            {isRoundTrip
              ? intl.formatMessage({
                  defaultMessage: "Total roundtrip price",
                  description:
                    "Label for the total price of the roundtrip flight in the fare selection step",
                })
              : intl.formatMessage({
                  defaultMessage: "Total one-way price",
                  description:
                    "Label for the total price of the one-way flight in the fare selection step",
                })}
          </p>

          <p className="text-base font-semibold text-text-primary">
            {formatCurrency(tripFare.totalPrice, tripFare.currency)}
          </p>
        </div>

        <Button
          fullWidth
          variant="contained"
          onClick={handleContinue}
          disabled={!tripFare}
        >
          {intl.formatMessage({
            defaultMessage: "Continue",
            description:
              "Primary button to proceed to the next flight booking step",
          })}
        </Button>
      </PersistentDrawer>
    </main>
  );
}
